/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext } from 'react';
import { ListItem, ListItemProps } from '@lesmills-international/components';
import { navigate } from 'gatsby';
import { useTheme } from '@mui/styles';
import { SectionTitle } from './styles';

import { userContext } from '../../../../gatsby-theme-engagement/src/context';
import { SignupChannel, SubscriptionType, isIAP, isReseller } from '../../utils/subscriptions';
import PaymentDetails from './PaymentDetails';
import ROUTES from '../../../../../src/constants/routes';
import mapPaymentName from '../../utils/normalizePaymentInfo';

type PageProps = Pick<
  Queries.PrismicAccountOverviewPageData & Queries.PrismicSignupChannelsData,
  | 'payment_section_title'
  | 'update_payment_details_title'
  | 'update_payment_details_description'
  | 'billing_history'
  | 'signup_channel'
>;

interface Props {
  data: PageProps;
}

const PaymentSection = ({ data }: Props) => {
  const { chargifyPaymentProfile, signupChannel, hasActiveSubscription, type } = useContext(userContext);
  const { themeColor } = useTheme();

  const {
    payment_section_title,
    update_payment_details_title,
    update_payment_details_description,
    billing_history,
    signup_channel,
  } = data;

  const isIAPUser = isIAP(signup_channel as unknown as SignupChannel[], signupChannel);
  const isResellerUser = isReseller(type as SubscriptionType);

  const paymentType = mapPaymentName(chargifyPaymentProfile);
  const hasExistingPayment = !!(chargifyPaymentProfile?.last4 || paymentType === 'paypal');

  // Active IAP users: billing is managed by the app store, not Chargify — hide the whole section.
  // All other users — including cancelled IAP and users with no current subscription —
  // should be able to update payment details (e.g. before resubscribing).
  if (isIAPUser && hasActiveSubscription) {
    return null;
  }

  const items: ListItemProps = [];

  // IAP billing is managed by the app store — there is no billing history in our system.
  if (!isIAPUser) {
    items.push({
      title: billing_history,
      onClick: () => navigate(ROUTES().BILLING_HISTORY),
    });
  }

  // Active resellers: billing is managed by the reseller — no Chargify payment details to update.
  if (!isResellerUser || !hasActiveSubscription) {
    items.unshift({
      title: update_payment_details_title,
      // Only show card description when a stored payment profile exists.
      description: hasExistingPayment ? (
        <PaymentDetails cardDescription={update_payment_details_description} />
      ) : undefined,
      onClick: () => navigate(ROUTES().UPDATE_PAYMENT_DETAILS),
    });
  }

  return (
    <div>
      <SectionTitle>{payment_section_title}</SectionTitle>
      <ListItem theme={themeColor} items={items} />
    </div>
  );
};

export default PaymentSection;
